import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, AlertCircle } from 'lucide-react';
import RetailerLayout from '../components/RetailerLayout';
import CmsBlocksRenderer from '../components/CmsBlocksRenderer';
import ProductCatalogView from '../components/catalog/ProductCatalogView';
import { Button } from '../components/ui/button';
import { getListingConfig, getHubConfig } from '../lib/pageConfig';
import useCmsOverlay from '../hooks/useCmsOverlay';

/**
 * ListingPage V4 — Page listing avec filtres catalogue intégrés (Phase 3.4.2)
 * 
 * ARCHITECTURE:
 * - CMS Header / bannières préservés (zones header, content, footer)
 * - ProductCatalogView avec apiEndpoint='/listings/{type}' pour les listings V2
 * - Fallback vers '/products' pour les listings legacy
 * - Filtres utilisateur combinés au contexte listing (backend AND)
 */
const ListingPage = () => {
  // Extraire slug et subPath depuis l'URL
  const pathParts = window.location.pathname.replace('/dashboard/', '').split('/');
  const slug = pathParts[0];
  const subPath = pathParts[1];
  const listingId = subPath ? `${slug}-${subPath}` : slug;
  
  const listingConfig = getListingConfig(listingId);
  const hubConfig = listingConfig?.hubId ? getHubConfig(listingConfig.hubId) : null;
  
  const { blocksByZone, hasCmsOverrides, cmsBlocks } = useCmsOverlay('listing', listingId, { debug: false });

  // Determine API endpoint and context filters
  const { apiEndpoint, contextFilters, initialSort } = useMemo(() => {
    if (!listingConfig) return { apiEndpoint: '/products', contextFilters: {}, initialSort: '' };
    
    if (listingConfig.listingType) {
      // V2 listing — use /api/listings/{type} which applies listing rules + accepts user filters
      return {
        apiEndpoint: `/listings/${listingConfig.listingType}`,
        contextFilters: {},
        initialSort: '',
      };
    }
    
    // Legacy fallback — use /api/products with listing-specific filters
    const filters = listingConfig.getFilters ? listingConfig.getFilters() : {};
    return {
      apiEndpoint: '/products',
      contextFilters: filters,
      initialSort: '',
    };
  }, [listingConfig]);

  // LISTING non trouvé
  if (!listingConfig) {
    return (
      <RetailerLayout>
        <div className="flex flex-col items-center justify-center py-20 px-4 min-h-[60vh]">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md text-center">
            <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">Page non trouvée</h2>
            <p className="text-gray-500 mb-6">La page "{listingId}" n'existe pas.</p>
            <Link to="/dashboard"><Button className="w-full">Retour au tableau de bord</Button></Link>
          </div>
        </div>
      </RetailerLayout>
    );
  }

  const { title, subtitle, breadcrumb, ui = {} } = listingConfig;

  // Formater la date pour l'affichage
  const formatDate = (isoDate) => {
    if (!isoDate) return null;
    try {
      return new Date(isoDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return null; }
  };

  // Optional extra render under each ProductCard (dates, remise)
  const renderProductExtra = (product) => (
    <>
      {ui.showDateSortie && product.date_sortie_iso && (
        <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {ui.dateLabel || 'Sortie le'} {formatDate(product.date_sortie_iso)}
        </div>
      )}
      {ui.showDateFin && (() => {
        // Phase 5: Lire date fin depuis ORU PRECOMMANDE, pas legacy
        const dists = product.distributions || [];
        const precoMech = dists.flatMap(d => (d.promotional_mechanics || []))
          .find(m => m.nature_offre === 'PRECOMMANDE' && (m.computed_status === 'ACTIVE' || !m.computed_status));
        const dateFin = precoMech?.date_fin;
        return dateFin ? (
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Jusqu&apos;au {formatDate(dateFin)}
          </div>
        ) : null;
      })()}
      {ui.showRemise && product.remise > 0 && (
        <div className="mt-2 text-xs font-semibold text-orange-600">
          -{product.remise}% de remise
        </div>
      )}
    </>
  );

  // Header content (breadcrumb + title + CMS)
  const listingHeader = (
    <>
      {/* Breadcrumb */}
      {breadcrumb && breadcrumb.length > 0 && (
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4" data-testid="listing-breadcrumb">
          <Link to="/dashboard" className="hover:text-gray-900 transition-colors">Accueil</Link>
          {breadcrumb.map((item, index) => (
            <React.Fragment key={index}>
              <span>/</span>
              {item.link ? (
                <Link to={item.link} className="hover:text-gray-900 transition-colors">{item.label}</Link>
              ) : (
                <span className="text-gray-900 font-medium">{item.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Title + Hub link */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
        </div>
        {hubConfig && (
          <Link to={`/dashboard/${listingConfig.hubId}`}>
            <Button variant="outline" size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour à {hubConfig.title}
            </Button>
          </Link>
        )}
      </div>

      {/* CMS content blocks */}
      {blocksByZone.content.length > 0 && (
        <div className="mb-4">
          <CmsBlocksRenderer blocks={blocksByZone.content} zone="content" pageType="listing" />
        </div>
      )}
    </>
  );

  return (
    <RetailerLayout>
      {/* CMS header zone (banners — full-width) */}
      {blocksByZone.header.length > 0 && (
        <CmsBlocksRenderer blocks={blocksByZone.header} zone="header" pageType="listing" />
      )}

      {/* Main content */}
      <div
        className="listing-page-container"
        data-testid={`listing-page-${listingId}`}
        style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 16px' }}
      >
        <ProductCatalogView
          contextFilters={contextFilters}
          initialSort={initialSort}
          apiEndpoint={apiEndpoint}
          header={listingHeader}
          emptyMessage={ui.emptyMessage || 'Aucun produit disponible pour le moment.'}
          renderProductExtra={(ui.showDateSortie || ui.showDateFin || ui.showRemise) ? renderProductExtra : undefined}
        />
      </div>

      {/* CMS footer zone */}
      {blocksByZone.footer.length > 0 && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 16px 32px' }}>
          <CmsBlocksRenderer blocks={blocksByZone.footer} zone="footer" pageType="listing" />
        </div>
      )}
    </RetailerLayout>
  );
};

export default ListingPage;
